import { PlusIcon } from '@heroicons/react/16/solid';
import React, { useState } from 'react';
import { twJoin } from 'tailwind-merge';
import { Hex } from 'viem';
import { useAccount } from 'wagmi';
import { DEFAULT_SUBACCOUNT } from '../../../constants';
import { SchemaSubaccountDto } from '../../../generated/apiTypes';
import { useModalState } from '../../../hooks/useModalState';
import { usePendingSubaccount, useSelectedSubaccount } from '../../../hooks/useSelectedSubaccount';
import { useSubaccounts } from '../../../hooks/useSubaccounts';
import { decodeSubaccountName, encodeSubaccountName } from '../../../lib/subaccountHelpers';
import { Button } from '../../catalyst-ui-kit/button';
import { Input } from '../../catalyst-ui-kit/input';
import { MultiSelect } from '../../MultiSelect/MultiSelect';

const DeveloperPanelUi = ({
  subaccounts,
  selectedSubaccount,
  setSubaccount,
}: {
  subaccounts: SchemaSubaccountDto[] | undefined;
  selectedSubaccount: Hex | SchemaSubaccountDto | null;
  setSubaccount: (subaccount: SchemaSubaccountDto | Hex) => void;
}) => {
  const [isCreatingSubaccount, setIsCreatingSubaccount] = useState(false);
  const [newSubaccountName, setNewSubaccountName] = useState('');
  const [inputError, setInputError] = useState(false);

  const options = [
    ...(typeof selectedSubaccount === 'string'
      ? [
          {
            value: selectedSubaccount,
            display: (
              <>
                {decodeSubaccountName(selectedSubaccount)}{' '}
                <span className="text-xs uppercase text-contentSecondary">(Pending)</span>
              </>
            ),
            label: `${decodeSubaccountName(selectedSubaccount)} (pending)`,
          },
        ]
      : []),
    ...(subaccounts
      ? subaccounts
          .map((subaccount) => ({
            value: subaccount,
            label: decodeSubaccountName(subaccount.name),
            isDefault: subaccount.name === DEFAULT_SUBACCOUNT,
          }))
          .sort((a, b) => {
            // Place default subaccount first
            if (a.isDefault) return -1;
            if (b.isDefault) return 1;

            // Otherwise, by creation date
            return a.value.createdAt - b.value.createdAt;
          })
      : []),
  ];

  const handleSubmit = () => {
    const pendingSubaccountName = newSubaccountName.trim()
      ? encodeSubaccountName(newSubaccountName.trim())
      : undefined;
    const isExisting = subaccounts?.find((subaccount) => subaccount.name === pendingSubaccountName);
    if (pendingSubaccountName && !isExisting) {
      setSubaccount(pendingSubaccountName);
      setIsCreatingSubaccount(false);
      setNewSubaccountName('');
    } else {
      setInputError(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsCreatingSubaccount(false);
      setNewSubaccountName('');
      setInputError(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 text-xs font-medium text-contentSecondary sm:p-4">
      <div className="flex items-center justify-between">
        <span>Subaccount</span>
        {options ? (
          <>
            {!isCreatingSubaccount ? (
              <MultiSelect
                options={options}
                multiselect={false}
                value={selectedSubaccount ?? undefined}
                onChange={(value) => {
                  if (value) setSubaccount(value);
                }}
                triggerLabel="Select Subaccount"
                showSearchInput={options.length > 5}
              >
                <div
                  className="flex cursor-pointer items-center gap-2 rounded p-2 px-4 text-center text-sm text-contentBlue transition-colors hover:bg-backgroundHoverOverlay"
                  role="status"
                  onClick={() => setIsCreatingSubaccount(true)}
                >
                  <PlusIcon className="size-4" />
                  New subaccount
                </div>
              </MultiSelect>
            ) : (
              <div className="flex">
                <Input
                  containerClassName={twJoin(
                    'w-36 rounded-r-none',
                    inputError ? '!border-contentRed' : ''
                  )}
                  className="text-sm"
                  placeholder="Subaccount name"
                  value={newSubaccountName}
                  onChange={(e) => {
                    setNewSubaccountName(e.target.value);
                    setInputError(false);
                  }}
                  onKeyDown={handleKeyDown}
                  autoFocus
                />
                <Button
                  color="blue"
                  onClick={handleSubmit}
                  disabled={!newSubaccountName.trim()}
                  className="rounded-l-none disabled:text-borderInverseTertiary disabled:!opacity-100"
                >
                  Create
                </Button>
              </div>
            )}
          </>
        ) : (
          <span>No subaccounts</span>
        )}
      </div>
    </div>
  );
};

export const DeveloperSettingsPanel: React.FC = () => {
  const {
    value: selectedSubaccount,
    setValue: setSelectedSubaccount,
    removeValue: removeSelectedSubaccount,
  } = useSelectedSubaccount();
  const { value: pendingSubaccount, setValue: setPendingSubaccount } = usePendingSubaccount();
  const { address } = useAccount();
  const { data: subaccounts } = useSubaccounts(address);
  const setSettingsModal = useModalState(({ setSettingsModal }) => setSettingsModal);
  const setDepositModal = useModalState(({ setDepositModal }) => setDepositModal);

  const setSubaccount = (subaccount: Hex | SchemaSubaccountDto) => {
    if (typeof subaccount === 'string') {
      setPendingSubaccount(subaccount);
      removeSelectedSubaccount();
      setDepositModal({ openOrClosed: 'open', context: 'Onboard' });
      setSettingsModal('closed');
    } else {
      setSelectedSubaccount(subaccount);
      setPendingSubaccount(null);
    }
  };

  return (
    <DeveloperPanelUi
      subaccounts={subaccounts?.data}
      selectedSubaccount={pendingSubaccount ?? selectedSubaccount}
      setSubaccount={setSubaccount}
    />
  );
};
